import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  query,
  where,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "hooks/auth/useAuth";
import { db } from "@freetech/models/db";
import {
  IFeatureRequest,
  ICreateFeatureRequest,
  FeatureRequestStatus,
  Comment,
  Attachment,
} from "@freetech/models";
import { useCallback } from "react";
import { useNonAdminClients } from "../clients/useNonAdminClients";
import {
  removeFeatureRequestAttachment,
  updateFeatureRequestAttachments,
} from "core/featureRequest";

export const useFeatureRequest = () => {
  const queryClient = useQueryClient();
  const { accessibleClientIds, clients } = useNonAdminClients();
  const { userInfo } = useAuth();

  const {
    data: featureRequests,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["featureRequests", accessibleClientIds],
    queryFn: async () => {
      if (!accessibleClientIds || accessibleClientIds.length === 0) {
        return [];
      }

      const q = query(
        db.featureRequests,
        where("clientId", "in", accessibleClientIds)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as IFeatureRequest[];
    },
    enabled: !!accessibleClientIds && accessibleClientIds.length > 0,
  });

  const getFeatureRequestsByClientId = useCallback(
    (clientId: string) => {
      return (
        featureRequests?.filter((request) => request.clientId === clientId) ||
        []
      );
    },
    [featureRequests]
  );

  const getFeatureRequestsByProjectId = useCallback(
    (projectId: string) => {
      return (
        featureRequests?.filter((request) => request.projectId === projectId) ||
        []
      );
    },
    [featureRequests]
  );

  const getFeatureRequestsByStatus = useCallback(
    (status: FeatureRequestStatus) => {
      return (
        featureRequests?.filter((request) => request.status === status) || []
      );
    },
    [featureRequests]
  );

  const getFeatureRequestById = useCallback(
    (id: string) => {
      return featureRequests?.find((request) => request.id === id);
    },
    [featureRequests]
  );

  const createFeatureRequest = useMutation({
    mutationFn: async (data: ICreateFeatureRequest) => {
      if (!accessibleClientIds?.includes(data.clientId)) {
        throw new Error(
          "You don't have permission to create feature requests for this client"
        );
      }

      const client = clients?.find((c) => c.id === data.clientId);
      if (!client) {
        throw new Error("Client not found");
      }

      const docRef = await addDoc(db.featureRequests, data);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["featureRequests"] });
    },
  });

  const updateFeatureRequest = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<IFeatureRequest>;
    }) => {
      const docRef = doc(db.featureRequests, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["featureRequests"] });
    },
  });

  const deleteFeatureRequest = useMutation({
    mutationFn: async (id: string) => {
      const docRef = doc(db.featureRequests, id);
      await deleteDoc(docRef);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["featureRequests"] });
    },
  });

  const approveFeatureRequest = useMutation({
    mutationFn: async (id: string) => {
      if (!userInfo?.id) throw new Error("User not authenticated");

      const docRef = doc(db.featureRequests, id);
      await updateDoc(docRef, {
        status: "approved" as FeatureRequestStatus,
        approvedByAdminUserId: userInfo.id,
        approvedByAdminDate: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["featureRequests"] });
    },
  });

  const rejectFeatureRequest = useMutation({
    mutationFn: async (id: string) => {
      if (!userInfo?.id) throw new Error("User not authenticated");

      const docRef = doc(db.featureRequests, id);
      await updateDoc(docRef, {
        status: "rejected" as FeatureRequestStatus,
        rejectedByAdminUserId: userInfo.id,
        rejectedByAdminDate: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["featureRequests"] });
    },
  });

  const addComment = useMutation({
    mutationFn: async ({
      featureRequestId,
      content,
      mentionedUserIds,
    }: {
      featureRequestId: string;
      content: string;
      mentionedUserIds?: string[];
    }) => {
      if (!userInfo) throw new Error("User info not found");

      const newComment: Comment = {
        id: crypto.randomUUID(),
        content,
        userId: userInfo.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        mentionedUserIds,
      };

      const docRef = doc(db.featureRequests, featureRequestId);

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("Feature request not found");
      }

      const currentFeatureRequest = docSnap.data() as IFeatureRequest;

      await updateDoc(docRef, {
        comments: [...(currentFeatureRequest.comments || []), newComment],
        updatedAt: new Date().toISOString(),
      });

      return newComment;
    },
    onSuccess: (_, { featureRequestId }) => {
      queryClient.invalidateQueries({
        queryKey: ["featureRequests"],
      });

      queryClient.invalidateQueries({
        queryKey: ["featureRequest", featureRequestId],
      });
    },
  });

  const removeAttachment = useMutation<
    void,
    Error,
    { featureRequestId: string; attachmentId: string; clientId: string }
  >({
    mutationFn: ({ featureRequestId, attachmentId, clientId }) =>
      removeFeatureRequestAttachment(clientId, featureRequestId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["featureRequests"],
      });
    },
  });

  const uploadAttachment = useMutation<
    void,
    Error,
    { featureRequestId: string; files: File[]; clientId: string }
  >({
    mutationFn: ({ featureRequestId, files, clientId }) =>
      updateFeatureRequestAttachments(clientId, featureRequestId, files),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["featureRequests"],
      });
    },
  });

  return {
    featureRequests,
    isLoading,
    error,
    getFeatureRequestsByClientId,
    getFeatureRequestsByProjectId,
    getFeatureRequestsByStatus,
    getFeatureRequestById,
    createFeatureRequest,
    updateFeatureRequest,
    deleteFeatureRequest,
    approveFeatureRequest,
    rejectFeatureRequest,

    addComment: addComment.mutateAsync,
    isAddingComment: addComment.isPending,

    removeAttachment: removeAttachment.mutateAsync,
    isRemovingAttachment: removeAttachment.isPending,
    uploadAttachment: uploadAttachment.mutateAsync,
    isUploadingAttachment: uploadAttachment.isPending,
  };
};
